/**
 * One icon, drawn with the game's own drawing code, embedded in the DOM.
 *
 * The order tickets have to show the same carrot the crate shows. Authoring
 * those twice — once as canvas paths and once as inline SVG — is how a ticket
 * ends up showing a vegetable that is not in the kitchen, so instead the HUD
 * mounts a small canvas per icon and calls the identical function `stations.ts`
 * calls.
 *
 * It draws once, on mount and whenever the inputs change. There is no animation
 * frame here: a ticket's ingredients do not move, and the phase-5 rule that no
 * 50 Hz value may go through React is not weakened by a static picture.
 */

import { useEffect, useRef } from 'react'

interface IconCanvasProps {
  /** Canvas-space size in CSS pixels. The draw call gets the same number. */
  size: number
  /** Called with a context already scaled for the device pixel ratio. */
  draw(ctx: CanvasRenderingContext2D, size: number): void
  /** Changing this redraws. Anything that affects the picture belongs in it. */
  signature: string
  className?: string
  /** Every icon is a picture of a thing that has a name. Screen readers get it. */
  title?: string
}

export function IconCanvas({ size, draw, signature, className, title }: IconCanvasProps) {
  const ref = useRef<HTMLCanvasElement>(null)
  const drawRef = useRef(draw)
  drawRef.current = draw

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    canvas.width = Math.round(size * dpr)
    canvas.height = Math.round(size * dpr)
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, size, size)
    drawRef.current(ctx, size)
  }, [size, signature])

  return (
    <canvas
      ref={ref}
      className={className}
      style={{ width: size, height: size }}
      role={title ? 'img' : 'presentation'}
      aria-label={title}
    />
  )
}
